import type { Container } from "pixi.js";
import { createStage } from "./render/Stage";
import { Line } from "./render/Line";
import { loadElement } from "./elements/loader";
import { TouchTracker } from "./interaction/TouchTracker";
import { getCsClient, type CsClient, type SetElementEvent, type SetModeEvent, type SetUvHzEvent, type SetMaxFingersEvent, type SetUvPackEvent, type SetShowEvent } from "./net/csClient";
import { PlaygroundClient } from "./playground/PlaygroundClient";
import { mountElementPicker } from "./playground/elementPicker";
import { mountDebugPanel } from "./playground/debugPanel";
import { SpringParams } from "./interaction/Spring";
import { proximityThresholdNm } from "./elements/settings";
import { ReleaseParams, HighlightParams, ThicknessEnvelopeParams } from "./render/Line";
import { SPECTRUM_BG_ALPHA_DEFAULT } from "./render/spectrumBackground";
import { RippleParams } from "./render/WaveField";
import { admitPhone, displayMode, displayLabel } from "./entry";
import { mountSeatGate } from "./gate";
import { t, applyLangAttribute } from "./i18n";
import { setShowScreen, SHOW_LIVE } from "./showscreen";
import { keepScreenAwake } from "./wakelock";

const params = new URLSearchParams(location.search);
// The debug/tuning panel and the local-audio playground are localhost-only:
// on a phone the visuals are driven entirely by the server, with no UI overlay.
// location.hostname is a loopback name only on the operator's own machine.
const IS_LOCALHOST = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[?::1\]?)$/.test(location.hostname);
const IS_PLAYGROUND = IS_LOCALHOST;
// The element is server-assigned. `?el=` is only a local override/placeholder;
// the real (phone) client starts blank and shows whatever the server pushes.
const elParam = (params.get("el") ?? "").trim();
const INITIAL_ELEMENT = elParam || (IS_PLAYGROUND ? "O" : "");
// Interaction mode starts from ?mode= (handy for local testing); the phone
// client is then overridden by the server's SET_MODE downlink on connect.
// Default is pad (whole-screen); pass ?mode=line to start in line mode.
const INITIAL_MODE: "line" | "pad" = params.get("mode") === "line" ? "line" : "pad";

async function boot() {
  const mount = document.getElementById("stage")!;
  const loadingOverlay = document.getElementById("loading-overlay");
  const loadingPct = document.getElementById("loading-pct");
  const loadingBar = document.getElementById("loading-bar");

  loadingOverlay?.classList.remove("hidden");
  if (loadingBar) loadingBar.style.width = "10%";

  const stage = await createStage(mount);
  if (loadingBar) loadingBar.style.width = "50%";

  // Mutable container so the playground client + tracker hold a stable
  // reference even as we swap elements.
  const linesRef = { current: [] as Line[] };

  let client: CsClient;
  if (IS_PLAYGROUND) {
    client = new PlaygroundClient(linesRef);
    document.title = "Cosmic Symphony — Playground";
  } else {
    try {
      client = await getCsClient();
    } catch (err) {
      console.warn("[cs] ws-client unavailable, using log mock:", err);
      client = mockClient();
    }
  }

  wireStatus(client);

  const tracker = new TouchTracker({
    stageEl: mount,
    lines: linesRef.current,
    client,
    width: stage.width,
    height: stage.height,
    mode: INITIAL_MODE,
    hooks: {
      onLineHit: (line, fingerY) => {
        stage.waveField.noteOn(line.index, line.restX, line.color, fingerY, line.restAlpha);
      },
      onLineMove: (line, fingerY) => {
        stage.waveField.setV(line.index, fingerY);
      },
      onLineRelease: (line) => {
        stage.waveField.noteOff(line.index);
      },
    },
  });

  // Debug-controllable globals (slider-driven).
  const debug = {
    thickness: 1,
    // Tuned default (see the localhost debug panel). Applied to the lines layer
    // in loadAndBuild and to the pre-bloom brightness at boot just below.
    intensity: 0.6,
    // null = use the element's per-element default threshold from settings.ts.
    bunchingOverride: null as number | null,
  };

  // Bake the tuned intensity in for everyone — the panel exists only on localhost,
  // so the phone client needs the pre-bloom brightness applied here directly.
  stage.brightness.brightness(debug.intensity, false);

  async function loadAndBuild(element: string) {
    if (loadingBar) loadingBar.style.width = "10%";
    loadingOverlay?.classList.remove("hidden");
    try {
      console.log("[swap]", element, "fetching...");
      const data = await loadElement(element, debug.bunchingOverride ?? undefined);
      console.log("[swap]", element, "got", data.length, "lines");
      destroyLines(linesRef.current, stage.linesLayer);
      const lines = data.map((d, i) =>
        new Line(d, i, stage.falloffTex, stage.width, stage.height, stage.linesLayer),
      );
      for (const line of lines) line.setThicknessMultiplier(debug.thickness);
      stage.linesLayer.alpha = debug.intensity;
      linesRef.current = lines;
      tracker.setLines(lines);
      if (loadingBar) loadingBar.style.width = "100%";
      loadingOverlay?.classList.add("hidden");
      console.log("[swap]", element, "done");
    } catch (err) {
      console.error("[swap] failed:", err);
      if (loadingPct) loadingPct.textContent = `Failed: ${(err as Error).message}`;
      loadingOverlay?.classList.add("hidden");
    }
  }

  // Serialised element swaps, shared by the playground picker and the
  // server-driven downlink so only one load runs at a time.
  const swap = { current: INITIAL_ELEMENT, loading: false };
  // Optional hook so the playground can keep its picker highlight in sync.
  let onSwap: ((el: string) => void) | null = null;
  async function requestSwap(element: string) {
    const el = (element ?? "").trim();
    if (!el || swap.loading || el === swap.current) return;
    swap.loading = true;
    swap.current = el;
    // Drop any user-set bunching override so the element's natural default shows.
    debug.bunchingOverride = null;
    onSwap?.(el);
    try {
      await loadAndBuild(el);
    } finally {
      swap.loading = false;
    }
  }

  // The real client starts blank and waits for the server's SET_ELEMENT; only
  // load up-front when we already have an element (playground, or ?el= override).
  // Nothing is written while it waits: the bar already says something is
  // happening, and the wait is normally milliseconds.
  if (INITIAL_ELEMENT) {
    await loadAndBuild(INITIAL_ELEMENT);
  }

  if (!IS_PLAYGROUND) {
    client.addEventListener("set-element", (ev) => {
      void requestSwap((ev as SetElementEvent).detail.element);
    });
    client.addEventListener("set-mode", (ev) => {
      tracker.setMode((ev as SetModeEvent).detail.mode as "line" | "pad");
    });
    client.addEventListener("set-uv-hz", (ev) => {
      tracker.setUvHz((ev as SetUvHzEvent).detail.uvHz);
    });
    client.addEventListener("set-max-fingers", (ev) => {
      tracker.setMaxFingers((ev as SetMaxFingersEvent).detail.maxFingers);
    });
    client.addEventListener("set-uv-pack", (ev) => {
      tracker.setUvPack((ev as SetUvPackEvent).detail.uvPack);
    });
    // Which screen this phone is on. The show itself is state 1 and has no
    // screen of its own — it IS the app — so this only ever puts something over
    // the top, or takes it away again.
    client.addEventListener("set-show", (ev) => {
      const next = (ev as SetShowEvent).detail.show;
      setShowScreen(next);
      // A phone covered by a screen is not playing, and a finger that was down
      // when the screen arrived would otherwise still be sounding a note that
      // nobody can now lift. The server drops its frames from here on, so the
      // release has to happen on this side or the note is stranded.
      if (next !== SHOW_LIVE) tracker.releaseAll();
    });
    // The ws client connects at import time, so open()'s push can beat these
    // listeners — apply whatever already arrived.
    if (client.element) void requestSwap(client.element);
    if (client.mode) tracker.setMode(client.mode as "line" | "pad");
    if (client.uvHz) tracker.setUvHz(client.uvHz);
    if (client.maxFingers) tracker.setMaxFingers(client.maxFingers);
    if (client.uvPack) tracker.setUvPack(client.uvPack);
    // Same reason as the four above: the socket opens at import time, so a phone
    // arriving during an interval has usually already been told which screen it
    // is on before there was anything here to hear it.
    if (client.show != null) setShowScreen(client.show);
  }

  stage.app.ticker.add((ticker) => {
    const dtSec = ticker.deltaMS / 1000;
    for (const line of linesRef.current) line.step(dtSec);
    stage.waveField.step(dtSec);
  });

  // Listen to the RENDERER, not to window 'resize'.
  //
  // Pixi's `resizeTo` does not resize synchronously: the window event only
  // queues the resize, and the renderer applies it on the next render. A window
  // listener therefore reads app.screen BEFORE it has changed, so every one of
  // the calls below ran with the previous size and nothing was ever laid out
  // for the size the user actually had. Measured in headless Chrome, resizing
  // 900x700 -> 480x900: inside the window handler `window.innerWidth` already
  // said 480 while the canvas still said 900x700. The canvas itself resized
  // fine, which is why this looked like "the stage ignores resize" rather than
  // an obvious crash: only the CONTENT was stale, and it stayed stale until the
  // next resize, where it picked up the size before last.
  //
  // The renderer's own 'resize' fires after the resize is applied and hands the
  // new size straight to us, so there is no ordering assumption left to get
  // wrong. It also covers resizes that no window event accompanies (the
  // `resizeTo` element changing size on its own, e.g. a phone's URL bar
  // collapsing or an orientation change that only reflows the container).
  stage.app.renderer.on("resize", (w: number, h: number) => {
    // stage.width/height are the size any FUTURE Line is built from — see
    // loadAndBuild above, which constructs the replacement spectrum from them
    // when the operator changes the element. They were captured once at
    // app.init() and never written again, so a swap after any rotation, URL-bar
    // collapse or fullscreen toggle laid the spectrum out for the geometry the
    // phone had at page load: a dead black band down one side, or a spectrum
    // cropped off the other. The live objects below were always updated, which
    // is what made this look like "changing the element breaks the stage"
    // rather than "the stage forgot how big it is".
    stage.width = w;
    stage.height = h;
    stage.background.width = w;
    stage.background.height = h;
    stage.waveField.setStageSize(w, h);
    tracker.setStageSize(w, h);
    for (const line of linesRef.current) line.resize(w, h);
  });

  // Mounted here rather than at the entry point below, and that placement is
  // the decision: mountSeatGate() runs INSTEAD of boot(), so scanning a seat
  // code stays a portrait job — which is how anyone would hold a phone at a
  // camera — and the landscape rule starts at the stage, where it means
  // something. It needs the tracker, too: the overlay stops the next touch, and
  // releaseAll ends the note already sounding from the last one.

  wireDevStats(tracker);

  // Only on the stage, and only once it exists. The seat gate does not take this
  // path and should not: a scanner nobody has pointed at a card yet has no claim
  // on somebody's battery.
  keepScreenAwake();

  if (IS_PLAYGROUND) {
    const picker = mountElementPicker(swap.current, (el) => { void requestSwap(el); });
    onSwap = (el) => picker.setActive(el);

    const initialFreqHz = Math.sqrt(SpringParams.k) / (2 * Math.PI);
    mountDebugPanel({
      initial: {
        thickness: debug.thickness,
        intensity: debug.intensity,
        bloom: stage.bloom.bloomScale,
        bloomThreshold: stage.bloom.threshold,
        vibrationFreq: initialFreqHz,
        vibrationGain: ReleaseParams.vibrationGain,
        bunching: proximityThresholdNm(INITIAL_ELEMENT),
        rippleSpeed: RippleParams.speed,
        rippleSpacing: RippleParams.spacing,
        rippleThickness: RippleParams.thicknessPx,
        rippleBrightness: RippleParams.brightness,
        rippleXOffset: RippleParams.xOffset,
        highlightIntensity: HighlightParams.intensity,
        envFloor: ThicknessEnvelopeParams.floor,
        envExponent: ThicknessEnvelopeParams.exponent,
        spectrumBrightness: SPECTRUM_BG_ALPHA_DEFAULT,
        ripples: RippleParams.enabled,
        sound: true,
      },
      onThickness: (mul) => {
        debug.thickness = mul;
        for (const line of linesRef.current) line.setThicknessMultiplier(mul);
      },
      onIntensity: (mul) => {
        debug.intensity = mul;
        // ColorMatrix brightness multiplies RGB pre-bloom. Values > 1 push the
        // bright cores into HDR territory, giving the bloom something to grab.
        stage.brightness.brightness(mul, false);
      },
      onBloom: (scale) => {
        stage.bloom.bloomScale = scale;
      },
      onBloomThreshold: (t) => {
        stage.bloom.threshold = t;
      },
      onVibrationFreq: (hz) => {
        // k = (2π·f)². cReleased scales as 2·ζ·√k so the damping ratio
        // stays roughly constant (ζ ≈ 0.08) — fast & slow alike die out
        // in a similar number of visible cycles.
        const k = (2 * Math.PI * hz) ** 2;
        SpringParams.k = k;
        SpringParams.cReleased = 0.16 * Math.sqrt(k);
        SpringParams.cHeld = 0.35 * Math.sqrt(k);
        SpringParams.kPull = 1.5 * k;
      },
      onVibrationGain: (gain) => {
        ReleaseParams.vibrationGain = gain;
      },
      onRippleSpeed: (v) => { RippleParams.speed = v; },
      onRippleSpacing: (v) => { RippleParams.spacing = v; },
      onRippleThickness: (v) => { RippleParams.thicknessPx = v; },
      onRippleBrightness: (v) => { RippleParams.brightness = v; },
      onRippleXOffset: (v) => { RippleParams.xOffset = v; },
      onHighlightIntensity: (v) => { HighlightParams.intensity = v; },
      onEnvFloor: (v) => { ThicknessEnvelopeParams.floor = v; },
      onEnvExponent: (v) => { ThicknessEnvelopeParams.exponent = v; },
      onSpectrumBrightness: (v) => { stage.background.alpha = v; },
      onRipples: (on) => { stage.waveField.setEnabled(on); },
      onSound: (enabled) => { (client as PlaygroundClient).setMuted(!enabled); },
      onBunching: async (thresholdNm) => {
        debug.bunchingOverride = thresholdNm;
        if (swap.loading) return;
        swap.loading = true;
        try {
          await loadAndBuild(swap.current);
        } finally {
          swap.loading = false;
        }
      },
    });
  }
}

function destroyLines(lines: Line[], parent: Container) {
  for (const line of lines) line.destroy(parent);
  lines.length = 0;
}

function mockClient(): CsClient {
  return {
    sendNoteOn: (line, finger) => console.log("[mock] NOTE_ON", line, finger),
    sendNoteOff: (line, finger) => console.log("[mock] NOTE_OFF", line, finger),
    sendFingerUV: (finger, u, v) =>
      console.log("[mock] UV", finger, u.toFixed(3), v.toFixed(3)),
    connected: true,
    addEventListener: () => {},
  };
}

function wireStatus(client: CsClient) {
  const dot = document.getElementById("status-dot");
  const text = document.getElementById("status-text");
  const overlay = document.getElementById("reconnect-overlay");
  const seatLabel = document.getElementById("seat-label");
  if (IS_PLAYGROUND) {
    if (seatLabel) seatLabel.textContent = "PLAY";
    if (dot) dot.className = "dot connected";
    if (text) text.textContent = "Playground";
    if (overlay) overlay.classList.add("hidden");
    return;
  }
  const w = window as unknown as { __csZone?: string; __csSeat?: number; __csSeatValid?: boolean };
  const zone = w.__csZone;
  const seat = w.__csSeat;
  const seatOk = w.__csSeatValid === true && typeof seat === "number" && Number.isInteger(seat) && seat >= 0;
  // What this phone is, in the hall's terms rather than in the token's.
  //
  // Every admitted phone holds a seat token — it is the only kind the socket
  // takes — so "A1" is always available to print and is often untrue. Under one
  // code for the house nobody chose a seat and nobody is sitting in the one they
  // were given; in a block entered on a single code the number is a slot the
  // server handed out. Telling somebody they are in A1 while they stand at the
  // back of the balcony is worse than telling them nothing, so the hall says
  // which of the three it is and this prints that.
  const mode = displayMode();
  // A name the hall handed over wins outright. Only one phone ever gets one — a
  // silent spectator, whose token says zone 254 and so cannot name itself without
  // printing a stray Latin letter — and the entire point of that name is that it
  // looks like everybody else’s, so nothing here may second-guess it.
  const given = displayLabel();
  if (seatLabel) {
    seatLabel.textContent =
        given            ? given                     // the hall said so
      : !seatOk          ? "—"                       // no valid token: never "-1"/"NaN"
      : mode === "live"  ? t("statusLive")           // nothing to print but that it is on
      : mode === "zone"  ? `${zone ?? "A"}`          // the block is real, the number is not
      :                    `${zone ?? "A"}${seat}`;  // both real, as it always was
  }
  // Once a state has held for a few seconds there is nothing left to read, so
  // the bar slides into the corner and leaves only the dot; the next change
  // brings it back to the middle with its word. The movement is what catches
  // the eye, which is the point: nobody is watching the top of the screen.
  const bar = document.getElementById("status-bar");
  const PARK_AFTER_MS = 3500;
  let parkTimer: ReturnType<typeof setTimeout> | undefined;
  const parkSoon = () => {
    bar?.classList.remove("parked");
    clearTimeout(parkTimer);
    parkTimer = setTimeout(() => bar?.classList.add("parked"), PARK_AFTER_MS);
  };

  let last: string | null = null;
  const setStatus = (state: "connecting" | "connected" | "disconnected") => {
    // Once a seat has been refused nothing else may repaint the bar: the socket
    // closing fires 'disconnected' on its way out, and without this the phone
    // would go straight back to promising a reconnection it is not attempting.
    if (last === "busy") return;
    const changed = state !== last;
    last = state;
    if (dot) dot.className = `dot ${state}`;
    if (text) {
      text.textContent = state === "connected" ? t("statusLive")
        : state === "connecting" ? t("statusConnect")
        : t("statusRetry");
    }
    if (overlay) overlay.classList.toggle("hidden", state === "connected");
    // A repeat of the same state is not news, and re-centring on every keepalive
    // would make the bar twitch for no reason.
    if (changed) parkSoon();
  };
  client.addEventListener("connecting", () => setStatus("connecting"));
  client.addEventListener("connected", () => setStatus("connected"));
  client.addEventListener("disconnected", () => setStatus("disconnected"));
  if (client.connected) setStatus("connected");
  else setStatus("connecting");

  // ── Seat busy ─────────────────────────────────────────────────────────────
  // Not a connection failure, and it must not be dressed as one: the seat is
  // taken and reconnecting will not untake it. The overlay switches mode, the
  // status bar stops saying "Reconnecting", and the way back in is a button the
  // person has to press.
  //
  // The button is on a cooldown because the honest answer to "why not retry
  // immediately" is that the other phone is still there. The server frees a
  // seat once its holder has been silent for about fifteen seconds, so the
  // cooldown is sized to that: press it the moment it lights up and the seat
  // has had a real chance to come free.
  const RETRY_COOLDOWN_S = 15;
  const busyText = document.getElementById("busy-text");
  const busyHint = document.getElementById("busy-hint");
  const busyBtn = document.getElementById("busy-retry") as HTMLButtonElement | null;
  const rescanBtn = document.getElementById("busy-rescan") as HTMLButtonElement | null;
  let cooldownTimer: ReturnType<typeof setInterval> | undefined;

  const armRetry = () => {
    if (!busyBtn) return;
    let left = RETRY_COOLDOWN_S;
    busyBtn.disabled = true;
    busyBtn.textContent = `${t("seatRetry")} (${left})`;
    clearInterval(cooldownTimer);
    cooldownTimer = setInterval(() => {
      left -= 1;
      if (left > 0) { busyBtn.textContent = `${t("seatRetry")} (${left})`; return; }
      clearInterval(cooldownTimer);
      busyBtn.disabled = false;
      busyBtn.textContent = t("seatRetry");
    }, 1000);
  };

  const showBusy = () => {
    last = "busy";
    // The real client starts blank and waits for the server's SET_ELEMENT, so a
    // phone that is never let in waits on "Loading… Waiting…" behind an opaque
    // black overlay — and never sees a word of the message below it. There is
    // nothing left to wait for, so the wait ends here.
    document.getElementById("loading-overlay")?.classList.add("hidden");
    if (dot) dot.className = "dot disconnected";
    if (text) text.textContent = t("seatBusy");
    if (busyText) busyText.textContent = t("seatBusy");
    if (busyHint) busyHint.textContent = t("seatBusyHint");
    if (rescanBtn) rescanBtn.textContent = t("seatRescan");
    overlay?.classList.remove("hidden");
    overlay?.classList.add("busy");
    // Leave the bar centred: this is the one state nobody should have to go
    // looking for in the corner.
    bar?.classList.remove("parked");
    clearTimeout(parkTimer);
    armRetry();
  };
  client.addEventListener("seat-busy", showBusy);

  // ── Removed ───────────────────────────────────────────────────────────────
  // An operator took this phone out of the show. Nothing to press and nothing to
  // wait for: the block is on this device and on the code it arrived with, so a
  // retry button would connect, be refused, and come straight back here. The one
  // real way out is a person, which is what the hint says.
  const goneText = document.getElementById("gone-text");
  const goneHint = document.getElementById("gone-hint");
  const showRemoved = () => {
    last = "busy";              // the same latch: nothing may repaint the bar now
    document.getElementById("loading-overlay")?.classList.add("hidden");
    if (dot) dot.className = "dot disconnected";
    if (text) text.textContent = t("removedTitle");
    if (goneText) goneText.textContent = t("removedTitle");
    if (goneHint) goneHint.textContent = t("removedHint");
    overlay?.classList.remove("hidden");
    overlay?.classList.remove("busy");
    overlay?.classList.add("gone");
    bar?.classList.remove("parked");
    clearTimeout(parkTimer);
    // Whatever cooldown a previous refusal armed is meaningless now.
    clearInterval(cooldownTimer);
  };
  client.addEventListener("removed", showRemoved);
  // Same reason as seatBusy below: the socket opens at import time, so a phone
  // that is refused on arrival has already fired this into an empty room.
  if (client.removed) showRemoved();

  // ── Opened in another tab ─────────────────────────────────────────────────
  // One phone plays from one place. The tab the person just opened took the
  // show, and this is the one they left behind — so the wording is about where
  // the show WENT, not about anything being wrong. Nothing here was refused and
  // nobody was removed.
  //
  // The button reloads rather than reconnecting through the client. Reconnecting
  // is exactly what must not happen automatically — two tabs each redialling on
  // being closed would evict each other all night — so taking the show back is
  // made a deliberate press, and a reload is also the honest way to do it: this
  // page comes up fresh, asks the door, and is handed the same seat back.
  const tabBtn = document.getElementById("tab-retake");
  const showAnotherTab = () => {
    last = "busy";              // the same latch: nothing may repaint the bar now
    document.getElementById("loading-overlay")?.classList.add("hidden");
    if (dot) dot.className = "dot disconnected";
    if (text) text.textContent = t("tabTitle");
    if (goneText) goneText.textContent = t("tabTitle");
    if (goneHint) goneHint.textContent = t("tabHint");
    if (tabBtn) tabBtn.textContent = t("tabRetake");
    overlay?.classList.remove("hidden");
    overlay?.classList.remove("busy", "gone");
    overlay?.classList.add("tab");
    bar?.classList.remove("parked");
    clearTimeout(parkTimer);
    clearInterval(cooldownTimer);
  };
  client.addEventListener("another-tab", showAnotherTab);
  // And read as a property too — more sharply than either of the two above. The
  // eviction is decided by the ARRIVING tab, so this one is closed within a
  // millisecond or two of the new tab opening its socket: reliably before this
  // UI exists to have heard the event.
  if (client.anotherTab) showAnotherTab();
  tabBtn?.addEventListener("click", () => location.reload());
  // The socket is opened at import time and a refusal comes back in
  // milliseconds, so by the time this UI exists the event has usually already
  // fired into an empty room. Same reason `client.connected` is read above.
  if (client.seatBusy) showBusy();

  busyBtn?.addEventListener("click", () => {
    if (busyBtn.disabled) return;
    overlay?.classList.remove("busy");
    clearInterval(cooldownTimer);
    // Lets the status bar speak again. Without this the guard above would hold
    // the phone on "seat busy" through a retry that in fact succeeded.
    last = null;
    setStatus("connecting");
    client.retrySeat?.();
  });

  // Back to the front of the app — the scanner and the "About the show" link —
  // for the likelier reading of this screen: not "the seat will free up" but
  // "I scanned the wrong chair".
  //
  // The token has to go first, and a reload on its own will not do it. The ws
  // client strips ?t= from the address on the first load and keeps the token in
  // sessionStorage, so hasSeatToken() would still be true and the page would
  // boot straight back into this same overlay. Cleared, the entry point takes
  // the seat-gate branch instead of boot(): no socket, just the camera and the
  // way out of here.
  //
  // replace(), not assign(), for the reason the gate uses it: a collision
  // screen for a seat somebody else is playing is not somewhere the back button
  // should lead.
  const leaveToGate = (sent: boolean) => {
    // The label goes with the token. remember() keeps an existing cs_display
    // across a reload (deliberately, so a block card is not forgotten) and
    // leaving it here would hand the next code somebody scans the last one's
    // word for where they are.
    //
    // The show code goes with them. It is carried on the card, so the next card
    // brings its own, and a phone that has been sent back here holding last
    // month's code would otherwise go on presenting it and be turned away by a
    // door that had nothing to do with why it was sent back.
    //
    // And, when an operator did this, a note that says so, read once by the
    // boot path: under one code for the house the bare address is itself the
    // card, so without it the door would let the phone straight back in with a
    // fresh seat, and Disconnect would be a reconnect with extra steps.
    try {
      sessionStorage.removeItem("cs_token");
      sessionStorage.removeItem("cs_display");
      sessionStorage.removeItem("cs_label");
      sessionStorage.removeItem("cs_show");
      if (sent) sessionStorage.setItem("cs_sentback", "1");
    } catch { /* private mode */ }
    location.replace(location.pathname);
  };
  rescanBtn?.addEventListener("click", () => leaveToGate(false));

  // ── Sent back ─────────────────────────────────────────────────────────────
  // An operator's Disconnect. The same exit as "scan another code", for the
  // same reasons, and read as a property too, like the three stop states
  // above: the close can land before this UI exists.
  const sentBack = () => leaveToGate(true);
  client.addEventListener("sent-back", sentBack);
  if (client.sentBack) sentBack();
}

function wireDevStats(tracker: TouchTracker) {
  const devStats = document.getElementById("dev-stats");
  const infoBtn = document.getElementById("info-btn");
  const fsBtn = document.getElementById("fs-btn");
  // Per-second packet counters are for the machine this is being built on. The
  // button stays in the markup so the playground can still reach them — and it
  // now has to be switched ON to appear, rather than relying on a `display:none`
  // that a later rule of equal specificity had been quietly beating for months.
  if (IS_PLAYGROUND) infoBtn?.classList.add("on");
  infoBtn?.addEventListener("click", () => devStats?.classList.toggle("hidden"));

  // iPhone Safari implements no Fullscreen API for elements at all (iPad does),
  // so this button was a control that silently did nothing on half the phones in
  // the hall. Feature-detect and take it away where it cannot work: on iOS the
  // route to a chromeless view is Add to Home Screen, which the
  // apple-mobile-web-app-capable meta already covers.
  type FsEl = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
  type FsDoc = Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
    webkitFullscreenElement?: Element | null;
  };
  const el = document.documentElement as FsEl;
  const doc = document as FsDoc;
  const enter = el.requestFullscreen ?? el.webkitRequestFullscreen;
  const exit  = doc.exitFullscreen ?? doc.webkitExitFullscreen;

  if (!enter) {
    fsBtn?.classList.add("hidden");
  } else {
    fsBtn?.addEventListener("click", () => {
      const isFull = !!(doc.fullscreenElement ?? doc.webkitFullscreenElement);
      // Both forms can reject (a gesture that arrived too late, a policy that
      // refuses); an unhandled rejection here would surface as a console error
      // on a phone nobody is looking at.
      try {
        const r = isFull ? exit?.call(doc) : enter.call(el);
        void Promise.resolve(r).catch(() => {});
      } catch { /* the button simply does nothing, as before */ }
    });
  }
  setInterval(() => {
    if (devStats && !devStats.classList.contains("hidden")) {
      devStats.textContent =
        `sent ${tracker.sentCount}/s\ndropped ${tracker.droppedCount}/s\ntotal ${tracker.totalCount}`;
    }
    tracker.sentCount = 0;
    tracker.droppedCount = 0;
  }, 1000);
}

// No seat, no app — but which of those this phone is, is the door's answer and
// not this file's. The socket is refused without a real seat token, so booting
// ahead of the door would buy a black screen and a spinner that never resolves.
applyLangAttribute();
const reconnectText = document.getElementById("reconnect-text");
if (reconnectText) reconnectText.textContent = t("statusRetry") + "…";

const startApp = () => boot().catch((err) => {
  console.error("[boot] failed:", err);
  const pct = document.getElementById("loading-pct");
  if (pct) pct.textContent = String(err?.message ?? err);
});

if (!IS_PLAYGROUND) {
  // Carrying a code or carrying nothing, the door is the one that knows what
  // that is worth. A code is not necessarily a seat — a hall hands out one for a
  // block or one for the house, and neither carries one — and no code at all is
  // not necessarily a mistake: under one code for the house the address IS the
  // card, so the person who scanned the poster, the person who typed it and the
  // person who heard it called out all arrive here holding the same nothing.
  //
  // So there is one path in, and the door decides which of its answers this
  // phone gets: a seat, the venue's link when it is shut, the scanner when the
  // hall prints cards, or a message when the code is no good. This used to be
  // two branches with the phone guessing which one applied, and it guessed
  // before the plan was known.
  //
  // Unless an operator sent this phone back a moment ago: then it is the
  // scanner, whatever the door would say. wireStatus() leaves the note as it
  // leaves; it is read once and cleared here, so a scan or a typed address
  // after it goes through the door as usual.
  let sentBack = false;
  try {
    sentBack = sessionStorage.getItem("cs_sentback") === "1";
    if (sentBack) sessionStorage.removeItem("cs_sentback");
  } catch { /* private mode */ }
  if (sentBack) mountSeatGate();
  else admitPhone().then(startApp);
} else {
  // The operator's own machine boots bare: no door, no gate, local audio.
  startApp();
}
